import type { FloorPlanSettings } from './floorPlanGeometry.logic.ts';
import { rackMeters, type FloorRack } from './floorPlan.logic.ts';
import type { Placement } from './placement.logic.ts';

export type FloorPlanProjectionRack = {
  id: string;
  label?: string;
  rowIndex: number;
  rackIndex: number;
  rowId?: string | null;
  moduleId?: string | null;
  placement: Placement;
  status?: string;
  role?: string;
};

export type FloorPlanProjectionRow = {
  id: string;
  labelIndex: number;
  name: string;
  moduleId?: string | null;
};

export type FloorPlanProjection = {
  version: 1;
  floorPlan: FloorPlanSettings;
  racks: FloorPlanProjectionRack[];
  rows: FloorPlanProjectionRow[];
  aisleZones: unknown[];
  structuralGeometry: unknown[];
  pathways: { nodes: unknown[]; edges: unknown[] };
};

type ProjectionRackInput = FloorRack & {
  id?: string | number;
  label?: string;
  rowId?: string | null;
  moduleId?: string | null;
  rowIndex?: number;
  rackIndex?: number;
  row_id?: string | null;
  module_id?: string | null;
  row_index?: number;
  rack_index?: number;
  status?: string;
  role?: string;
  orientation?: number | null;
};

export function buildFloorPlanProjection(input: {
  floorPlan: FloorPlanSettings;
  racks?: ProjectionRackInput[] | Record<string, ProjectionRackInput>;
  rows?: FloorPlanProjectionRow[] | Record<string, FloorPlanProjectionRow>;
  aisleZones?: unknown[];
  structuralGeometry?: unknown[];
  pathways?: { nodes?: unknown[]; edges?: unknown[] } | null;
}): FloorPlanProjection {
  const racks = Array.isArray(input.racks) ? input.racks : Object.values(input.racks ?? {});
  const rows = Array.isArray(input.rows) ? input.rows : Object.values(input.rows ?? {});

  return {
    version: 1,
    floorPlan: { ...input.floorPlan },
    racks: racks
      .filter(rack => rack.id != null)
      .map(rack => {
        const metric = rackMeters(rack);
        return {
          id: String(rack.id),
          ...(rack.label ? { label: rack.label } : {}),
          rowIndex: rack.rowIndex ?? rack.row_index ?? 0,
          rackIndex: rack.rackIndex ?? rack.rack_index ?? 0,
          rowId: rack.rowId ?? rack.row_id ?? null,
          moduleId: rack.moduleId ?? rack.module_id ?? null,
          placement: {
            xM: metric.x,
            yM: metric.y,
            widthM: metric.w,
            depthM: metric.h,
            orientation: rack.placement?.orientation ?? rack.orientation ?? rack.rotation ?? 0,
          },
          ...(rack.status ? { status: rack.status } : {}),
          ...(rack.role ? { role: rack.role } : {}),
        };
      }),
    rows: rows.map(row => ({
      id: String(row.id),
      labelIndex: row.labelIndex,
      name: row.name,
      moduleId: row.moduleId ?? null,
    })),
    aisleZones: [...(input.aisleZones ?? [])],
    structuralGeometry: [...(input.structuralGeometry ?? [])],
    pathways: {
      nodes: [...(input.pathways?.nodes ?? [])],
      edges: [...(input.pathways?.edges ?? [])],
    },
  };
}

export function projectionBounds(projection: FloorPlanProjection) {
  const { originXMm, originYMm, widthMm, heightMm } = projection.floorPlan;
  return {
    minXM: originXMm / 1000,
    minYM: originYMm / 1000,
    maxXM: (originXMm + widthMm) / 1000,
    maxYM: (originYMm + heightMm) / 1000,
  };
}
